/**
 * @file: ProfileHeader.tsx
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 *
 * Who the member is, at the top of the Me home screen. The stack's own nav
 * header stays pinned above it and carries the settings and balance controls.
 * On phones and tablets the profile is a brand-coloured block continuing that
 * header; on desktop, where the header is white, it is a plain card.
 */

import { Typography } from '@euler/app/components/typography';
import { MeIcon } from '@euler/app/flows/me/components/MeIcon';
import {
  MK,
  cardShadowStyle,
  kHeroOverlap,
} from '@euler/app/flows/me/components/meKit';
import { maskedMobilePhone } from '@euler/app/tv/functions/maskedMobilePhone';
import { Avatar } from '@euler/components/Avatar';
import { PredefinedHitSlops } from '@euler/functions/predefinedHitSlops';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useIsDesktopLayout } from '@euler/functions/responsiveness';
import { memo } from 'react';
import { StyleProp, TouchableOpacity, View, ViewStyle } from 'react-native';

export type ProfileSummaryProps = {
  name: string;
  avatar?: string;
  mobile?: string | null;
  jobTitle?: string | null;
  roles: string[];
  onPress: () => void;
  /** Shown only in the hero; desktop has help in the sidebar. */
  onHelpPress?: () => void;
  contentStyle?: StyleProp<ViewStyle>;
};

export const ProfileHero = memo((props: ProfileSummaryProps) => {
  const { rpx } = useResponsiveContext();
  return (
    <View
      style={{
        backgroundColor: MK.brand,
        paddingBottom: rpx(24 + kHeroOverlap),
      }}
    >
      {/* Fills the gap revealed when the scroll view bounces past the top,
          so the pull-to-refresh area stays brand-coloured. */}
      <View
        pointerEvents="none"
        style={{
          position: 'absolute',
          left: 0,
          right: 0,
          top: -1000,
          height: 1000,
          backgroundColor: MK.brand,
        }}
      />
      <TouchableOpacity
        activeOpacity={0.8}
        onPress={props.onPress}
        style={[
          {
            flexDirection: 'row',
            alignItems: 'center',
            paddingHorizontal: rpx(20),
            paddingTop: rpx(8),
          },
          props.contentStyle,
        ]}
      >
        <AvatarRing
          name={props.name}
          avatar={props.avatar}
          size={rpx(76)}
          ring={rpx(4)}
          ringColor={MK.onBrandFill}
        />
        <ProfileDetails {...props} variant="hero" />
      </TouchableOpacity>
    </View>
  );
});

export const ProfileCard = memo((props: ProfileSummaryProps) => {
  const { rpx } = useResponsiveContext();
  return (
    <TouchableOpacity
      activeOpacity={0.8}
      onPress={props.onPress}
      style={[
        {
          flexDirection: 'row',
          alignItems: 'center',
          backgroundColor: MK.surface,
          borderRadius: rpx(16),
          borderWidth: 1,
          borderColor: MK.hairline,
          padding: rpx(20),
        },
        cardShadowStyle(rpx),
      ]}
    >
      <AvatarRing
        name={props.name}
        avatar={props.avatar}
        size={rpx(68)}
        ring={rpx(3)}
        ringColor={MK.brandSoft}
      />
      <ProfileDetails {...props} variant="card" />
      <MeIcon name="alt-arrow-right" size={rpx(18)} color={MK.mutedInk} />
    </TouchableOpacity>
  );
});

const ProfileDetails = memo(
  ({
    name,
    mobile,
    jobTitle,
    roles,
    onHelpPress,
    variant,
  }: ProfileSummaryProps & { variant: 'hero' | 'card' }) => {
    const { rpx } = useResponsiveContext();
    const isHero = variant === 'hero';
    const strong = isHero ? MK.onBrand : MK.ink;
    const dim = isHero ? MK.onBrandDim : MK.mutedInk;
    const phone = maskedMobilePhone(mobile);
    return (
      <View style={{ flex: 1, minWidth: 0, marginLeft: rpx(16) }}>
        <View style={{ flexDirection: 'row', alignItems: 'center' }}>
          {/* The name keeps its room; a long job title gives way first. */}
          <View
            style={{
              flex: 1,
              minWidth: 0,
              flexDirection: 'row',
              alignItems: 'baseline',
            }}
          >
            <Typography.Text
              size={rpx(20)}
              lineHeight={rpx(26)}
              color={strong}
              bold
              numberOfLines={1}
              style={{ flexShrink: 0, maxWidth: '70%' }}
            >
              {name}
            </Typography.Text>
            {!!jobTitle && (
              <Typography.Text
                size={rpx(14)}
                color={dim}
                ml={rpx(8)}
                numberOfLines={1}
                style={{ flexShrink: 1 }}
              >
                {jobTitle}
              </Typography.Text>
            )}
          </View>
          {isHero && onHelpPress != null && (
            <TouchableOpacity
              onPress={onHelpPress}
              hitSlop={PredefinedHitSlops.large}
              accessibilityLabel="帮助中心"
              style={{ marginLeft: rpx(12) }}
            >
              <MeIcon name="question-circle" size={rpx(20)} color={strong} />
            </TouchableOpacity>
          )}
        </View>
        {!!phone && (
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              marginTop: rpx(4),
            }}
          >
            <MeIcon name="phone" size={rpx(14)} color={dim} />
            <Typography.Text size={rpx(13)} color={dim} ml={rpx(6)}>
              {phone}
            </Typography.Text>
          </View>
        )}
        {roles.length > 0 && (
          <View
            style={{
              flexDirection: 'row',
              flexWrap: 'wrap',
              gap: rpx(6),
              marginTop: rpx(10),
            }}
          >
            {roles.map(role => (
              <View
                key={role}
                style={{
                  borderRadius: rpx(6),
                  backgroundColor: isHero ? MK.onBrandFill : MK.brandSoft,
                  paddingHorizontal: rpx(8),
                  paddingVertical: rpx(3),
                }}
              >
                <Typography.Text
                  size={rpx(12)}
                  color={isHero ? MK.onBrand : MK.brand}
                  medium
                >
                  {role}
                </Typography.Text>
              </View>
            ))}
          </View>
        )}
      </View>
    );
  },
);

const AvatarRing = memo(
  ({
    name,
    avatar,
    size,
    ring,
    ringColor,
  }: {
    name: string;
    avatar?: string;
    size: number;
    ring: number;
    ringColor: string;
  }) => {
    const outer = size + ring * 2;
    return (
      <View
        style={{
          width: outer,
          height: outer,
          borderRadius: outer / 2,
          backgroundColor: ringColor,
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Avatar
          uri={avatar}
          name={name}
          bold
          textStyle={{ fontSize: size * 0.4, lineHeight: size * 0.55 }}
          style={{ width: size, height: size, borderRadius: size / 2 }}
        />
      </View>
    );
  },
);

/**
 * Nav-header left slot. Desktop has settings in the sidebar, so the button is
 * only needed on phones and tablets.
 */
export const MeHeaderSettingsButton = memo(
  ({ onPress }: { onPress: () => void }) => {
    const { rpx } = useResponsiveContext();
    const isDesktop = useIsDesktopLayout();
    if (isDesktop) return null;
    return (
      <TouchableOpacity
        onPress={onPress}
        hitSlop={PredefinedHitSlops.large}
        accessibilityLabel="设置"
        style={{
          marginLeft: rpx(16),
          width: rpx(34),
          height: rpx(34),
          borderRadius: rpx(10),
          backgroundColor: MK.onBrandFill,
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <MeIcon name="settings" size={rpx(20)} color={MK.onBrand} />
      </TouchableOpacity>
    );
  },
);

/**
 * Nav-header right slot: the withdrawable balance. On desktop, where the
 * left slot and the hero are absent, help and settings sit here too.
 */
export const MeHeaderRight = memo(
  ({
    balance,
    onBalancePress,
    onHelpPress,
    onSettingsPress,
  }: {
    balance?: string;
    onBalancePress?: () => void;
    onHelpPress: () => void;
    onSettingsPress: () => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const isDesktop = useIsDesktopLayout();
    if (!isDesktop && balance == null) return null;
    return (
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          gap: rpx(16),
          marginRight: rpx(16),
        }}
      >
        {isDesktop && (
          <>
            <TouchableOpacity
              onPress={onHelpPress}
              hitSlop={PredefinedHitSlops.large}
              accessibilityLabel="帮助中心"
            >
              <MeIcon name="question-circle" size={rpx(20)} color={MK.ink} />
            </TouchableOpacity>
            <TouchableOpacity
              onPress={onSettingsPress}
              hitSlop={PredefinedHitSlops.large}
              accessibilityLabel="设置"
            >
              <MeIcon name="settings" size={rpx(20)} color={MK.ink} />
            </TouchableOpacity>
          </>
        )}
        {balance != null && (
          <BalancePill balance={balance} onPress={onBalancePress} />
        )}
      </View>
    );
  },
);

/** The withdrawable balance, tappable to withdraw. */
const BalancePill = memo(
  ({ balance, onPress }: { balance: string; onPress?: () => void }) => {
    const { rpx } = useResponsiveContext();
    const isDesktop = useIsDesktopLayout();
    const color = isDesktop ? MK.success : MK.onBrand;
    return (
      <TouchableOpacity
        onPress={onPress}
        disabled={onPress == null}
        hitSlop={PredefinedHitSlops.medium}
        accessibilityLabel={`可提现 ${balance}`}
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          borderRadius: rpx(999),
          backgroundColor: isDesktop ? MK.successSoft : MK.onBrandFill,
          paddingHorizontal: rpx(12),
          paddingVertical: rpx(5),
        }}
      >
        <MeIcon name="wallet-money" size={rpx(16)} color={color} />
        <Typography.Text size={rpx(12)} color={color} bold ml={rpx(6)}>
          {balance}
        </Typography.Text>
      </TouchableOpacity>
    );
  },
);
